import { apiPreboarding } from "@/api/preboarding.api.ts";
import useErrorHandle from "@/hooks/use-error-handle.hook.tsx";
import { employeesKeys } from "@/queries/employees/use-employees.query.ts";
import { ApiErrorException } from "@/types/api.types.ts";
import { ApiEmployeePortalAccessReleasePayload } from "@/types/preboarding.types.ts";
import { createQueryKeys } from "@lukemorales/query-key-factory";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

export const preboardingKeys = createQueryKeys("preboarding", {
  home: {
    queryKey: null,
    queryFn: async ({ signal }) => {
      const result = await apiPreboarding.getHome(signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  },
  employeeAccess: (employeeId: number) => ({
    queryKey: [employeeId],
    queryFn: async ({ signal }) => {
      const result = await apiPreboarding.getEmployeePortalAccess(employeeId, signal);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
  }),
});

export const usePreboardingHome = () => {
  return useQuery(preboardingKeys.home);
};

export const useEmployeePortalAccess = (employeeId: number, enabled: boolean = true) => {
  return useQuery({
    ...preboardingKeys.employeeAccess(employeeId),
    enabled,
    staleTime: 60 * 1000,
  });
};

export const useInvalidateEmployeePortalAccess = (employeeId: number) => {
  const queryClient = useQueryClient();

  return () => {
    void queryClient.invalidateQueries({
      queryKey: preboardingKeys.employeeAccess(employeeId).queryKey,
    });
    void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    void queryClient.invalidateQueries({ queryKey: employeesKeys.me.queryKey });
  };
};

export const usePreboardingCompleteTask = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiPreboarding.completeTask(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
      void queryClient.invalidateQueries({ queryKey: employeesKeys.me.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const usePreboardingStartTask = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiPreboarding.startTask(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const usePreboardingStopTask = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (taskId: number) => {
      const result = await apiPreboarding.stopTask(taskId);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const usePreboardingAttachTaskFile = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, formData }: { taskId: number; formData: FormData }) => {
      const result = await apiPreboarding.attachTaskFile(taskId, formData);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const usePreboardingDetachTaskFile = () => {
  const { onError } = useErrorHandle();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({ taskId, fileUuid }: { taskId: number; fileUuid: string }) => {
      const result = await apiPreboarding.detachTaskFile(taskId, fileUuid);
      if (result.error) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: preboardingKeys.home.queryKey });
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};

export const useReleaseEmployeePortalAccess = (employeeId: number) => {
  const { onError } = useErrorHandle();
  const invalidatePortalAccess = useInvalidateEmployeePortalAccess(employeeId);

  return useMutation({
    mutationFn: async (payload: ApiEmployeePortalAccessReleasePayload) => {
      const result = await apiPreboarding.releaseEmployeePortalAccess(employeeId, payload);
      if (result.error || !result.data) throw new ApiErrorException(result.error);
      return result.data;
    },
    onSuccess: () => {
      invalidatePortalAccess();
    },
    onError: (error: Error) => {
      onError(error);
    },
  });
};
